import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAvatarFace } from "./AppAvatarFace";
import { AppMediaRules } from "./AppMediaRules";
import { AppSource } from "./AppSource";

// AppAvatars - the contacts' real WhatsApp profile pictures in the app's own lists (2026-09-27, owner:
// "find the avatar for a user - it is crucial for the genuine WhatsApp experience").
//
// ⭐ EVERY ROW GETS A DISC; THE PICTURE IS LAID ON IT WHEN IT ARRIVES. The disc carries the initials
// (or WhatsApp's head-and-shoulders silhouette for a title with none - a bare number; `AppAvatarFace`,
// one markup with the contact page's header), so a contact with no picture - hidden by their privacy
// setting, none set, not fetched yet, or an old index - looks like WhatsApp's own grey disc, and a
// picture that fails to load leaves exactly that. ⛔ Never a broken image: the picture is a
// BACKGROUND of the disc, set only once its bytes are in hand.
//
// ⚠ WHY THE APP FETCHES AT ALL: the picture is `People/X/Avatar.jpg` in the subscriber's Dropbox, and
// Dropbox wants the read ticket in a HEADER an `<img>` cannot send (`AppMediaRules`' header). So each
// one is read with the ticket (`AppSource.LoadMedia`) and shown from a blob URL.
// ⚠ LAZY AND BOUNDED: only rows that come into view are fetched (`IntersectionObserver`, 200px ahead),
// at most `MaxAtOnce` at a time, and each picture ONCE per visit - the blob URL is kept for the
// session, so re-rendering the list (every keystroke in the search box) costs nothing.
// ⛔ A FAILED PICTURE IS FATE 1: the disc keeps its initials, the next one is fetched, nothing is
// reported, and that picture is not asked for again this visit.
// ⛔ Only a value `AppMediaRules.Resolve` produced for `Avatar.jpg` is ever read (`AppIndex`).

/** What this needs from a disc - an `Element` satisfies it by shape. */
export interface IAvatarEl {
    getAttribute: (Name: string) => string | null;
    setAttribute: (Name: string, Value: string) => void;
    /** ⛔ The picture goes on through the CSSOM - the CSP refuses a `style` attribute. */
    style: IAvatarStyle;
}

export interface IAvatarStyle { backgroundImage: string; }

/** What this needs from the list - an `Element` / `Document` satisfies it by shape. */
export interface IAvatarRoot {
    querySelectorAll: (Selector: string) => ArrayLike<IAvatarEl>;
}

export class AppAvatarRules {
    /** The attribute a disc carries its picture's folder href in. */
    public static readonly Attr: string = "data-wac-avatar";
    public static readonly MaxAtOnce: number = 3;

    /** Up to two initials, or "" when the title has none (a bare number) - `AppAvatarFace.LettersOf`. */
    public static InitialsOf = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppAvatarFace.LettersOf(Title);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarRules.InitialsOf");
            return "";
        }
    };

    /**
     * One disc. `Href` "" (no picture) = the initials alone, with no attribute - nothing to fetch.
     * ⛔ `Href` must be `<folder>/Avatar.jpg`, or the disc is drawn without it.
     * ⛔ NEVER AN EMPTY CIRCLE: a title with no initials gets the silhouette (`AppAvatarFace.Inner`).
     */
    public static Disc = (Title: string, Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppAvatarRules.Span("av", Href) + AppAvatarFace.Inner(Title) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarRules.Disc");
            return "";
        }
    };

    /**
     * A small disc for a task row: when there IS a picture, or when the contact has no name (the
     * silhouette says "unknown number" at a glance). A named contact with no picture gets nothing -
     * initials at 18 px would be clutter. ⛔ The silhouette sits UNDER a picture too, so one that fails
     * to load leaves a face, never an empty circle.
     */
    public static Small = (Href: string, Title: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ No title at all ("") is "not told", not "nameless" - a caller that names nobody draws nothing.
            const Nameless: boolean = nz(Title).trim().length > 0 && AppAvatarFace.IsNameless(Title);
            if (AppAvatarRules.HrefOk(Href) === false && Nameless === false) { return ""; }
            return AppAvatarRules.Span("av sm", Href) + AppAvatarFace.Silhouette() + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarRules.Small");
            return "";
        }
    };

    /**
     * The opening tag. ⛔ NO `style` IN THE MARKUP (the CSP has no 'unsafe-inline' for styles): a picture this
     * visit already fetched is painted by `AppAvatarLoader.Fill` from its cache, in the same task as the paint
     * that drew this markup - so a list re-render (a filter, a tick) still never flashes back to initials.
     */
    private static Span = (Cls: string, Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "<span>"; }
        try {
            if (AppAvatarRules.HrefOk(Href) === false) { return "<span class=\"" + Cls + "\" aria-hidden=\"true\">"; }
            return "<span class=\"" + Cls + "\" aria-hidden=\"true\" " + AppAvatarRules.Attr + "=\"" + kHTMLCore.EscapeAttr(Href) + "\">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarRules.Span");
            return "<span>";
        }
    };

    /** DOCUMENTED EXCEPTION: pure predicate. ERROR-SUPPRESSED-JUSTIFIED: false is the safe answer. */
    public static HrefOk = (Href: string): boolean => {
        const Text: string = nz(Href);
        const Tail: string = "/" + AppMediaRules.AvatarFile;
        if (Text.length <= Tail.length || Text.substring(Text.length - Tail.length) !== Tail) { return false; }
        return Text.indexOf("..") < 0 && Text.indexOf(":") < 0 && Text.charAt(0) !== "/";
    };
}

/** One picture in flight - a class, so its answer knows which href it was for (no closures). */
export class AppAvatarFetch {
    private Loader: AppAvatarLoader;
    private Href: string;

    public constructor(Loader: AppAvatarLoader, Href: string) {
        this.Loader = Loader;
        this.Href = Href;
    }

    public OnUrl = (Url: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a blob URL arriving must be kept track of.
        try {
            this.Loader.Landed(this.Href, Url);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFetch.OnUrl");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: a picture not synced yet or an expired ticket is fate 1 (header). */
    public OnFail = (Why: string): void => {
        try {
            this.Loader.Landed(this.Href, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFetch.OnFail");
        }
    };
}

/**
 * Fetches and lays pictures on the discs of one archive. ⚠ ONE PER SOURCE: a new sign-in or archive
 * gets a new loader (`AppMain.Faces`), so a picture from one account can never land on another's list.
 */
export class AppAvatarLoader {
    /**
     * The pictures of the CURRENT loader, for markup drawn after they arrived (`AppAvatarRules.Span`).
     * ⚠ Reset by every new loader, so a new source never shows the old one's pictures.
     */
    public static Shown: Record<string, string> = {};

    private Source: AppSource;
    /** href -> blob URL, for the whole visit. */
    private Cache: Record<string, string> = {};
    /** hrefs that failed this visit - not asked again. */
    private Failed: Record<string, boolean> = {};
    /** href -> the discs waiting for it (a list re-render adds more for the same href). */
    private Waiting: Record<string, IAvatarEl[]> = {};
    private Queue: string[] = [];
    private InFlight: Record<string, boolean> = {};
    private Active: number = 0;
    /**
     * One watcher PER AREA (the list, the AI panel's header ...). ⛔ Never one shared watcher: filling the panel header
     * used to disconnect the list's, so the list's pictures waited for its next repaint (owner 2026-10-26: "considerable
     * delay"). A repaint of an area replaces only that area's watcher.
     */
    private Watches: Map<IAvatarRoot, IntersectionObserver> = new Map<IAvatarRoot, IntersectionObserver>();
    /** Requests made - for the suite. */
    public Asked: number = 0;

    public constructor(Source: AppSource) {
        this.Source = Source;
        AppAvatarLoader.Shown = {};
    }

    /** Every disc under `Root`: painted at once from the cache, else fetched when it comes into view. */
    public Fill = (Root: IAvatarRoot | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null) { return; }
            const Old: IntersectionObserver | undefined = this.Watches.get(Root);
            if (Old !== undefined) { Old.disconnect(); this.Watches.delete(Root); }
            let Watch: IntersectionObserver | null = null;
            if (typeof IntersectionObserver !== "undefined") {
                Watch = new IntersectionObserver(this.OnSeen, { rootMargin: "200px" });
                this.Watches.set(Root, Watch);
            }
            const Found: ArrayLike<IAvatarEl> = Root.querySelectorAll("[" + AppAvatarRules.Attr + "]");
            for (let At: number = 0; At < Found.length; At = At + 1) { this.One(Found[At], Watch); }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.Fill");
        }
    };

    private One = (El: IAvatarEl, Watch: IntersectionObserver | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Href: string = nz(El.getAttribute(AppAvatarRules.Attr));
            if (AppAvatarRules.HrefOk(Href) === false || this.Failed[Href] === true) { return; }
            const Url: string | undefined = this.Cache[Href];
            if (Url !== undefined) { AppAvatarLoader.Paint(El, Url); return; }
            if (Watch === null) { this.Want(El); return; }
            Watch.observe(El as unknown as Element);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.One");
        }
    };

    public OnSeen = (Entries: IntersectionObserverEntry[], Watch: IntersectionObserver): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Entry of Entries) {
                if (Entry.isIntersecting !== true) { continue; }
                Watch.unobserve(Entry.target);
                this.Want(Entry.target as unknown as IAvatarEl);
            }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.OnSeen");
        }
    };

    /** Queue a disc's picture - once per href, however many discs wait on it. */
    private Want = (El: IAvatarEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Href: string = nz(El.getAttribute(AppAvatarRules.Attr));
            const Url: string | undefined = this.Cache[Href];
            if (Url !== undefined) { AppAvatarLoader.Paint(El, Url); return; }
            let Discs: IAvatarEl[] | undefined = this.Waiting[Href];
            if (Discs === undefined) { Discs = []; this.Waiting[Href] = Discs; }
            Discs.push(El);
            if (this.InFlight[Href] === true || this.Queue.indexOf(Href) >= 0) { return; }
            this.Queue.push(Href);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.Want");
        }
    };

    /** At most `MaxAtOnce` requests at a time, in the order the rows came into view. */
    private Next = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            while (this.Active < AppAvatarRules.MaxAtOnce && this.Queue.length > 0) {
                const Href: string = this.Queue.shift() as string;
                this.InFlight[Href] = true;
                this.Active = this.Active + 1;
                this.Asked = this.Asked + 1;
                const Fetch: AppAvatarFetch = new AppAvatarFetch(this, Href);
                this.Source.LoadMedia(Href, Fetch.OnUrl, Fetch.OnFail);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.Next");
        }
    };

    /** A picture's answer: `Url` "" = failed (the discs keep their initials). */
    public Landed = (Href: string, Url: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the queue must move on whatever happened.
        try {
            delete this.InFlight[Href];
            this.Active = Math.max(0, this.Active - 1);
            let Discs: IAvatarEl[] = [];
            if (this.Waiting[Href] !== undefined) { Discs = this.Waiting[Href]; }
            delete this.Waiting[Href];
            if (Url.length === 0) { this.Failed[Href] = true; this.Next(); return; }
            this.Cache[Href] = Url;
            AppAvatarLoader.Shown[Href] = Url;
            for (const El of Discs) { AppAvatarLoader.Paint(El, Url); }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarLoader.Landed");
        }
    };

    /**
     * The picture as the disc's background; `has` hides the initials under it.
     * DOCUMENTED EXCEPTION: a CSSOM write and a class write on an element in hand. ⚠ The URL is ours (a `blob:`
     * or, from the local source, a folder URL) - it is quoted, and quotes, backslashes, parentheses and
     * line breaks are removed from it (none occur in either).
     * ERROR-SUPPRESSED-JUSTIFIED: the callers are guarded.
     */
    public static Paint = (El: IAvatarEl, Url: string): void => {
        El.style.backgroundImage = AppAvatarLoader.ImageFor(Url);
        let Cls: string = nz(El.getAttribute("class"));
        if ((" " + Cls + " ").indexOf(" has ") < 0) { Cls = (Cls + " has").trim(); }
        El.setAttribute("class", Cls);
    };

    /** DOCUMENTED EXCEPTION: pure string. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static ImageFor = (Url: string): string => {
        return "url(\"" + nz(Url).replace(/["\\()\n\r]/g, "") + "\")";
    };
}
